import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Image as ImageIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  FactCheck as StructuredIcon,
  ShortText as UnstructuredIcon,
  Edit as EditIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { MedicationDetailDto } from '../../../services/admin-medication.service';
import { getImageUrl } from '../../../utils/image-url.util';

interface MedicationDetailDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  data: MedicationDetailDto | null;
  isLoading: boolean;
  getFormLabel: (value: string) => string;
}

export const MedicationDetailDialog: React.FC<MedicationDetailDialogProps> = ({
  open,
  onClose,
  onEdit,
  data,
  isLoading,
  getFormLabel
}) => {
  if (!data && !isLoading) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Деталі препарату #{data?.id}
        {data?.isSystem && (
          <Tooltip title="Системний елемент">
            <VerifiedIcon color="primary" />
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography>Завантаження...</Typography>
        ) : data ? (
          <Grid container spacing={3}>
            {/* Header with Image and Basic Info */}
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Avatar
                variant="rounded"
                src={data.imageUrl ? getImageUrl(data.imageUrl) : undefined}
                alt="Medication Image"
                sx={{ width: 150, height: 150, margin: '0 auto', mb: 2 }}
              >
                <ImageIcon sx={{ fontSize: 60 }} />
              </Avatar>
              
              <Box sx={{ mb: 2 }}>
                <Chip 
                  icon={data.isStructured ? <StructuredIcon /> : <UnstructuredIcon />} 
                  label={data.isStructured ? "Структурований" : "Неструктурований"} 
                  color={data.isStructured ? "success" : "default"} 
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
                <br />
                <Chip 
                  label={getFormLabel(data.form)} 
                  color="primary" 
                  size="small" 
                />
              </Box>

              {data.externalLink && (
                <Button 
                  startIcon={<LinkIcon />} 
                  href={data.externalLink} 
                  target="_blank" 
                  size="small"
                >
                  Зовнішнє посилання
                </Button>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              {/* Translations Table */}
              <Typography variant="h6" gutterBottom>Назви (Переклади)</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Мова</TableCell>
                      <TableCell>Назва</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(data.nameI18n).map(([lang, name]) => (
                      <TableRow key={lang}>
                        <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px' }}>{lang}</TableCell>
                        <TableCell>{name || <Typography variant="caption" color="text.secondary">Немає перекладу</Typography>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Details / Description Table */}
              {data.detailsI18n && Object.keys(data.detailsI18n).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>Опис / Інструкція</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Мова</TableCell>
                          <TableCell>Опис</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(data.detailsI18n).map(([lang, text]) => (
                          <TableRow key={lang}>
                            <TableCell sx={{ textTransform: 'uppercase', fontWeight: 'bold', width: '60px', verticalAlign: 'top' }}>{lang}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                              {text || <Typography variant="caption" color="text.secondary">Порожньо</Typography>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Dosage Info */}
              <Typography variant="h6" gutterBottom>Дозування</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {data.dosage && data.dosage.length > 0 ? (
                  data.dosage.map((d, index) => (
                    <Chip key={index} label={d} variant="outlined" />
                  ))
                ) : (
                  <Typography color="text.secondary">Не вказано</Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                {/* Author Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> Автор
                  </Typography>
                  {data.creator ? (
                    <Box>
                      <Typography variant="body1" fontWeight="bold">{data.creator.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">{data.creator.email}</Typography>
                      <Typography variant="caption" color="text.secondary">ID: {data.creator.id}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" color="text.secondary">Система</Typography>
                      {data.isSystem && <VerifiedIcon color="primary" fontSize="small" />}
                    </Box>
                  )}
                </Grid>

                {/* Statistics */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon /> Статистика
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Використано разів</Typography>
                      <Typography variant="h5" color="primary">{data.usageCount}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Створено</Typography>
                      <Typography variant="body2">{new Date(data.createdAt).toLocaleString()}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Оновлено</Typography>
                      <Typography variant="body2">{new Date(data.updatedAt).toLocaleString()}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Typography color="error">Не вдалося завантажити дані</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onEdit} startIcon={<EditIcon />} color="primary" variant="contained">
          Редагувати
        </Button>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
};
